import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Drawer, Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import TextArea from "../../components/Inputs/TextAreaInput";
import { deleteFacility } from "../../services/facilityService";
import { getPartnerTypes } from "../../services/partnerTypeService";
import {
  createPartner,
  getPartners,
  updatePartner,
} from "../../services/partnerService";
import { partnerSchema } from "../../validators/partnerValidators";
import { BiShow } from "react-icons/bi";
import { getSessions } from "../../services/sessionService";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Partner = () => {
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [partnerToEdit, setPartnerToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedPartnerType, setSelectedPartnerType] =
    useState("Not Selected");

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [sessions, setSessions] = useState([]);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadPartners();
    loadInstitutions();
    loadPartnerTypes();
    loadSessions();
  }, []);

  useEffect(() => {
    let filtered = partners;

    if (searchTerm.trim() !== "") {
      filtered = partners.filter((item) =>
        `${item.name} ${item.institution.name} ${item.partner_type.name} ${item.project}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedPartnerType !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.partner_type?.id?.toString() === selectedPartnerType?.toString()
      );
    }

    setFilteredPartners(filtered);
  }, [searchTerm, partners, selectedInstitution, selectedPartnerType]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadSessions = async () => {
    try {
      const { data } = await getSessions();
      setSessions(data.data);
    } catch (error) {
      toast.error("Failed to load sessions");
    }
  };

  const loadPartnerTypes = async () => {
    try {
      const { data } = await getPartnerTypes();
      setPartnerTypes(data.data);
    } catch (error) {
      toast.error("Failed to load partner types");
    }
  };

  const loadPartners = async () => {
    setLoading(true);
    try {
      const { data } = await getPartners();
      setPartners(data.data);
      setFilteredPartners(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (partnerToEdit) {
      onUpdatePartner(payload, resetForm);
    } else {
      onCreatePartner(payload, resetForm);
    }
  };

  const onCreatePartner = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createPartner(payload);

      setPartners((prev) => [...prev, data.data]);

      toast.success("Partners added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response.data.error || "Partner was not added, try again!");
      }
    }
  };

  const handleEdit = (category) => {
    setPartnerToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdatePartner = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalPartners = [...partners];

      const partnerIndex = originalPartners.findIndex(
        (partner) => partner.id === partnerToEdit.id
      );

      if (partnerIndex === -1) throw new Error("Partners not found.");

      const { data } = await updatePartner(partnerToEdit.id, payload);

      originalPartners[partnerIndex] = data.data;

      setPartners(originalPartners);

      setPartnerToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Partner updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Partner not updated, try again!");
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setPartnerToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeletePartner = async () => {
    const originalPartners = [...partners];

    const newPartners = originalPartners.filter(
      (partner) => partner.id !== partnerToDelete.id
    );

    setPartners(newPartners);

    try {
      await deleteFacility(partnerToDelete?.id);

      toast.success("Partner deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Partner not deleted, try again later"
        }`
      );

      setPartners(originalPartners);
    } finally {
      setIsDeleteModalOpen(false);

      setPartnerToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setPartnerToEdit(null);
    setPartnerToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },

    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Year",
      dataIndex: ["session", "name"],
      key: "session",
      render: (text) => (
        <span className="text-gray-700 text-sm">{text || "N/A"}</span>
      ),
    },
    {
      title: "Partner",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Partner Type",
      dataIndex: "partner_type",
      key: "partner_type",
      render: (_, record) => record?.partner_type?.name,
    },

    {
      title: "Partner Industry",
      dataIndex: "partner_industry",
      key: "partner_industry",
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Partnership">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedPartner(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Partnership">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Partnership">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Partnership" subtitle1="Partnership" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Partnership"
            count={filteredPartners.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Partnership"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredPartners.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                )}

                <Select
                  multiple={false}
                  id="institutions"
                  defaultValue={selectedPartnerType}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedPartnerType(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Partner Type
                  </Select.Option>
                  {partnerTypes &&
                    partnerTypes.length > 0 &&
                    partnerTypes.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {partnerToEdit ? "Edit Partnership" : "Add Partnership"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: partnerToEdit ? partnerToEdit?.name : "",
                partner_type_id: partnerToEdit
                  ? partnerToEdit?.partner_type?.id
                  : "",

                institution_id: partnerToEdit
                  ? partnerToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                partner_industry: partnerToEdit
                  ? partnerToEdit?.partner_industry
                  : "",

                project: partnerToEdit ? partnerToEdit?.project : "",
                description: partnerToEdit ? partnerToEdit?.description : "",
                session_id: partnerToEdit ? partnerToEdit?.session?.id : "",
              }}
              enableReinitialize
              validationSchema={partnerSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          partnerToEdit
                            ? partnerToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}
                    <CustomDropDownInput
                      name="session_id"
                      title="Year"
                      placeholder="Select a year"
                      requiredField
                      errors={errors}
                      values={values}
                      touched={touched}
                      displayLabel="name"
                      idLabel="id"
                      options={sessions}
                      setFieldValue={setFieldValue}
                    />

                    <TextInput
                      name="name"
                      title="Partner"
                      placeholder="e.g. RENU"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <CustomDropDownInput
                      name="partner_type_id"
                      title="Partner Type"
                      placeholder="Select a partner type"
                      requiredField
                      errors={errors}
                      values={values}
                      touched={touched}
                      displayLabel="name"
                      idLabel="id"
                      options={partnerTypes}
                      setFieldValue={setFieldValue}
                    />

                    <TextInput
                      name="partner_industry"
                      title="Partner Industry"
                      placeholder="e.g. Aviation"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextInput
                      name="project"
                      title="Project"
                      placeholder="e.g. Construction Project"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextArea
                      name="description"
                      title="Description"
                      placeholder="e.g. Construction Project"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeletePartner}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          <Drawer
            title={
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedPartner?.name || "Partner Details"}
                </h3>
                <span className="text-xs text-gray-500">
                  {selectedPartner?.partner_type?.name}
                </span>
              </div>
            }
            width={500}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedPartner ? (
              <div className="text-sm text-gray-700 space-y-4 px-1">
                <p>
                  <strong>Year:</strong>

                  <span className="text-gray-600 whitespace-pre-line">
                    {" "}
                    {selectedPartner?.session?.name || "No Year provided."}
                  </span>
                </p>
                <p>
                  <strong>Institution:</strong>{" "}
                  {selectedPartner?.institution?.name || "—"}
                </p>
                <p>
                  <strong>Industry:</strong>{" "}
                  {selectedPartner?.partner_industry || "—"}
                </p>
                <p>
                  <strong>Project:</strong> {selectedPartner?.project || "—"}
                </p>
                <p>
                  <strong>Description:</strong>
                  <br />
                  <span className="text-gray-600 whitespace-pre-line">
                    {selectedPartner?.description || "No description provided."}
                  </span>
                </p>
              </div>
            ) : (
              <p className="text-gray-500">No partner selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default Partner;
